import { useCallback } from 'react';
import axiosClient from 'api/axiosClient';
import useNotificationsStore from 'hooks/store/notifications/useNotificationsStore';
import { NotificationFeedDocument } from 'components/notification-popup/notificationFeedItem';

/** Rows per dropdown page. Matches what the web app requests and stays well
 * under the backend's hard ceiling of 100. */
export const NOTIFICATION_FEED_PAGE_SIZE = 10;

export interface ParsedNotificationFeed {
  docs: NotificationFeedDocument[];
  page: number;
  pages: number;
  total: number;
}

const EMPTY_PAGE: ParsedNotificationFeed = { docs: [], page: 1, pages: 0, total: 0 };

const asCount = (value: unknown, fallback: number) =>
  Number.isInteger(value) && (value as number) >= 0 ? (value as number) : fallback;

/**
 * The `POST /notification/list` envelope to a shape the store can hold without
 * further checks. Anything unexpected degrades to an empty page rather than
 * throwing, so a malformed payload cannot take the dropdown down with it.
 */
export const parseNotificationFeedResponse = (response: any): ParsedNotificationFeed => {
  const body = response?.data;

  if (!body || body.error === true) {
    return EMPTY_PAGE;
  }

  const payload = body?.data;

  if (!payload) {
    return EMPTY_PAGE;
  }

  return {
    docs: Array.isArray(payload.docs) ? payload.docs : [],
    page: asCount(payload.page, 1),
    pages: asCount(payload.pages, 0),
    total: asCount(payload.total, 0),
  };
};

/** A backend failure arrives as a 200 with `error: true` as often as it does as
 * a rejection, so both have to raise the same error state. */
const isFailedResponse = (response: any) => !response?.data || response.data.error === true;

export default function useGetNotificationFeed() {
  const beginFeedRequest = useNotificationsStore(state => state.beginFeedRequest);
  const applyFeedPage = useNotificationsStore(state => state.applyFeedPage);
  const applyFeedError = useNotificationsStore(state => state.applyFeedError);
  const applyFeedRefresh = useNotificationsStore(state => state.applyFeedRefresh);
  const endFeedRefresh = useNotificationsStore(state => state.endFeedRefresh);

  const fetchNotificationFeed = useCallback(
    async ({ page, isLoadMore = false }: { page: number; isLoadMore?: boolean }): Promise<void> => {
      const requestId = beginFeedRequest(isLoadMore);

      try {
        const response = await axiosClient.post('/notification/list', {
          page,
          limit: NOTIFICATION_FEED_PAGE_SIZE,
        });

        if (isFailedResponse(response)) {
          applyFeedError({ requestId, isLoadMore });
          return;
        }

        const parsed = parseNotificationFeedResponse(response);

        applyFeedPage({ requestId, ...parsed, isLoadMore });
      } catch (error) {
        applyFeedError({ requestId, isLoadMore });
      }
    },
    [applyFeedError, applyFeedPage, beginFeedRequest]
  );

  /**
   * Background poll for new arrivals, driven by the unread-count socket push
   * while the dropdown is open. Deliberately not routed through
   * beginFeedRequest: the reader is looking at the list, so this must not set
   * the loading flags, and a failure must not swap their rows for an error.
   * Skipping a failed refresh is safe because the next arrival triggers
   * another one.
   */
  const refreshNotificationFeed = useCallback(async (): Promise<void> => {
    const requestId = useNotificationsStore.getState().beginFeedRefresh();

    try {
      const response = await axiosClient.post('/notification/list', {
        page: 1,
        limit: NOTIFICATION_FEED_PAGE_SIZE,
      });

      if (isFailedResponse(response)) {
        return;
      }

      const { docs, pages, total } = parseNotificationFeedResponse(response);

      applyFeedRefresh({ requestId, docs, pages, total });
    } catch (error) {
      // Intentionally silent, see above.
    } finally {
      // Always released, so a failed refresh cannot block the next request.
      endFeedRefresh();
    }
  }, [applyFeedRefresh, endFeedRefresh]);

  return { fetchNotificationFeed, refreshNotificationFeed };
}
